import { AlertTriangle, Info } from "lucide-react";
import * as React from "react";
import { strategyText, type CadenceFormValues, formFromBody } from "@/lib/cadence";
import {
  busiestDay, clockWords, planCalls, planNotes, type Moved, type Plan, type PlannedCall,
} from "@/lib/cadence-plan";
import type { CadenceBody } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * What a cadence does to a lead who never picks up, drawn as it happens: a calendar of the days
 * with a cell per day shaded by its calls, the calls of the day under the pointer on an 8 am to
 * 9 pm line, and the things worth knowing in words. It is recomputed on every change, so an
 * admin sees a wait they moved move the calls.
 */

/** A phase's colour on the calendar and the day line, by its place in the cadence. */
export const PHASE_COLORS = ["bg-accent-9", "bg-gray-9", "bg-accent-6", "bg-gray-6", "bg-accent-11", "bg-gray-11"] as const;
export const phaseColor = (i: number) => PHASE_COLORS[i % PHASE_COLORS.length]!;

const SHADES = ["bg-gray-3", "bg-accent-4", "bg-accent-6", "bg-accent-8", "bg-accent-10"] as const;
const shade = (n: number) => SHADES[Math.min(n, SHADES.length - 1)]!;

const JOIN_TIMES = [
  { minute: 9 * 60, label: "9 am" },
  { minute: 13 * 60, label: "1 pm" },
  { minute: 19 * 60, label: "7 pm" },
] as const;

const MOVED: Record<Moved, string> = {
  quiet_hours: "held for the calling hours",
  daily_cap: "held by the daily cap",
  replanned: "the second call was late, so a new cycle",
};

const WEEKS_SHOWN = 13;

export function endWords(v: CadenceFormValues, plan: Plan): string {
  switch (plan.endedBy) {
    case "total_attempts":
      return `All ${plan.calls.length} calls made by day ${plan.lastDay}`;
    case "expired":
      return `Stops ${v.expire_days.trim()} days after the lead joins`;
    case "phases_over": {
      const last = v.phases[v.phases.length - 1];
      return `Stops ${last?.ends_days.trim() || plan.lastDay} days after the lead joins`;
    }
    default:
      return "Still calling a year on";
  }
}

export function CadencePreview({ values, compact = false }: { values: CadenceFormValues; compact?: boolean }) {
  const [expanded, setExpanded] = React.useState(false);
  const [join, setJoin] = React.useState<number>(JOIN_TIMES[0].minute);
  const plan = React.useMemo(() => planCalls(values, { joinMinute: join }), [values, join]);
  const uncapped = React.useMemo(
    () => (values.daily_calls_per_number.trim() ? planCalls({ ...values, daily_calls_per_number: "" }, { joinMinute: join }) : undefined),
    [values, join],
  );
  const notes = React.useMemo(() => planNotes(values, plan, uncapped), [values, plan, uncapped]);
  const [picked, setPicked] = React.useState(1);
  const [hovered, setHovered] = React.useState<number | null>(null);
  const day = Math.min(hovered ?? picked, Math.max(1, plan.lastDay));
  const busiest = busiestDay(plan);
  const shownDays = Math.min(Math.max(7, plan.lastDay), WEEKS_SHOWN * 7);
  const weeks = Math.ceil(shownDays / 7);
  const phaseOnDay = (d: number) => {
    const i = plan.phases.findIndex((p) => d >= p.fromDay && (p.toDay === null || d <= p.toDay));
    return i === -1 ? null : i;
  };

  return (
    <section aria-label="What a lead gets" className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-text">What a lead gets</h2>
          <p className="text-xs text-text-muted">If they never pick up.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-text-subtle">Joins at</span>
          <div className="flex rounded-md border border-border p-0.5" role="group" aria-label="When the lead joins">
            {JOIN_TIMES.map((t) => (
              <button
                key={t.minute}
                type="button"
                aria-pressed={t.minute === join}
                onClick={() => setJoin(t.minute)}
                className={cn(
                  "rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
                  t.minute === join ? "bg-accent-9 text-white dark:text-gray-1" : "text-text-muted hover:text-text",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <dl className="grid grid-cols-3 divide-x divide-border border-b border-border">
        <Stat label="Calls in all" value={String(plan.calls.length)} />
        <Stat label="Busiest day" value={`${busiest.calls}`} unit={busiest.calls === 1 ? "call" : "calls"} />
        <Stat label="Last call" value={plan.calls.length ? `Day ${plan.calls[plan.calls.length - 1]!.day}` : "—"} />
      </dl>

      {compact && !expanded ? (
        <div className="space-y-2 px-4 py-3">
          <p className="text-xs font-medium text-text">{endWords(values, plan)}</p>
          <div className="flex gap-0.5" aria-hidden>
            {Array.from({ length: 28 }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "h-4 min-w-0 flex-1 rounded-[3px]",
                  i + 1 > plan.lastDay ? "border border-dashed border-border" : shade(plan.perDay[i] ?? 0),
                )}
              />
            ))}
          </div>
          <button type="button" onClick={() => setExpanded(true)} className="text-xs font-medium text-accent-11 hover:underline">
            Day by day
          </button>
        </div>
      ) : (
      <>
      <div className="space-y-3 px-4 py-3">
        <p className="text-xs font-medium text-text">{endWords(values, plan)}</p>
        <div
          className="grid max-w-sm gap-1"
          style={{ gridTemplateColumns: "2.25rem repeat(7, minmax(0, 1fr))" }}
          onMouseLeave={() => setHovered(null)}
        >
          {Array.from({ length: weeks }, (_, w) => (
            <React.Fragment key={w}>
              <span className="self-center text-[10px] font-medium uppercase tracking-wide text-text-subtle">Wk {w + 1}</span>
              {Array.from({ length: 7 }, (_, k) => {
                const d = w * 7 + k + 1;
                const n = plan.perDay[d - 1] ?? 0;
                const beyond = d > plan.lastDay;
                const phase = beyond ? null : phaseOnDay(d);
                const selected = d === day;
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={beyond}
                    onMouseEnter={() => !beyond && setHovered(d)}
                    onFocus={() => setHovered(d)}
                    onBlur={() => setHovered(null)}
                    onClick={() => setPicked(d)}
                    aria-label={`Day ${d}: ${n} call${n === 1 ? "" : "s"}`}
                    className={cn(
                      "relative flex aspect-square min-h-7 flex-col items-center justify-center overflow-hidden rounded-md text-[10px] tabular-nums transition-shadow",
                      beyond ? "border border-dashed border-border bg-transparent text-text-subtle" : shade(n),
                      !beyond && n >= 3 ? "text-white dark:text-gray-1" : "text-text-muted",
                      selected && "ring-2 ring-accent-9 ring-offset-1 ring-offset-surface",
                    )}
                  >
                    <span className="leading-none">{d}</span>
                    {n > 0 && <span className="mt-0.5 text-[11px] font-semibold leading-none">{n}</span>}
                    {phase !== null && <span className={cn("absolute inset-x-0 bottom-0 h-0.5", phaseColor(phase))} aria-hidden />}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        {plan.lastDay > shownDays && (
          <p className="text-[11px] text-text-subtle">
            … and {plan.lastDay - shownDays} more days, {plan.calls.filter((c) => c.day > shownDays).length} calls.
          </p>
        )}
        <Legend plan={plan} />
      </div>

      <DayLine day={day} calls={plan.calls.filter((c) => c.day === day)} values={values} />
      </>
      )}

      {notes.length > 0 && (
        <ul className="space-y-2 border-t border-border px-4 py-3">
          {notes.map((n) => (
            <li key={n.text} className="flex gap-2 text-xs">
              {n.tone === "warning" ? (
                <AlertTriangle className="mt-px size-3.5 shrink-0 text-warning-11" aria-hidden />
              ) : (
                <Info className="mt-px size-3.5 shrink-0 text-accent-11" aria-hidden />
              )}
              <span className={n.tone === "warning" ? "text-text" : "text-text-muted"}>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-border bg-surface-sunken px-4 py-2 text-[11px] text-text-subtle">
        Calls go out 8 am to 9 pm on the lead&apos;s clock, the campaign default. No other campaign is counted.
      </p>
    </section>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="px-4 py-2.5">
      <dt className="text-[11px] text-text-subtle">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-text">
        {value}
        {unit && <span className="ml-1 text-xs font-normal text-text-muted">{unit}</span>}
      </dd>
    </div>
  );
}

function Legend({ plan }: { plan: Plan }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted">
      <span className="inline-flex items-center gap-1">
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className={cn("size-2.5 rounded-sm", shade(n))} aria-hidden />
        ))}
        <span className="ml-0.5">0–3+ calls</span>
      </span>
      {plan.phases.map((p, i) => (
        <span key={p.name} className="inline-flex items-center gap-1">
          <span className={cn("h-0.5 w-3 rounded-full", phaseColor(i))} aria-hidden />
          <span className="font-mono">{p.name}</span>
        </span>
      ))}
    </div>
  );
}

const LINE_START = 8 * 60;
const LINE_END = 21 * 60;
const TICKS = [8 * 60, 12 * 60, 16 * 60, 20 * 60];

function DayLine({ day, calls, values }: { day: number; calls: PlannedCall[]; values: CadenceFormValues }) {
  const x = (minute: number) => `${((minute - LINE_START) / (LINE_END - LINE_START)) * 100}%`;
  return (
    <div className="space-y-2.5 border-t border-border px-4 py-3">
      <p className="text-xs font-medium text-text">
        Day {day}
        <span className="ml-1.5 font-normal text-text-muted">
          {calls.length === 0 ? "no calls" : `${calls.length} call${calls.length === 1 ? "" : "s"}`}
        </span>
      </p>
      <div className="relative mx-1 h-11" aria-hidden>
        <div className="absolute inset-x-0 top-[22px] h-1 rounded-full bg-gray-3" />
        {TICKS.map((t) => (
          <span key={t} className="absolute top-[30px] -translate-x-1/2 text-[10px] text-text-subtle" style={{ left: x(t) }}>
            {clockWords(t).replace(":00", "")}
          </span>
        ))}
        {calls.map((c, i) => {
          // Calls minutes apart would sit on one another: stack them upwards instead.
          const stack = calls.slice(0, i).filter((p) => c.minute - p.minute < 12).length;
          return (
            <span
              key={`${c.at}-${i}`}
              className={cn(
                "absolute size-3 -translate-x-1/2 rounded-full ring-2 ring-surface",
                c.strategy === "unbranded" || c.strategy === "rotate"
                ? "border-2 border-accent-9 bg-surface"
                : phaseColor(c.phase),
              )}
              style={{ left: x(c.minute), top: 18 - stack * 7 }}
            />
          );
        })}
      </div>
      {calls.length > 0 && (
        <ol className="space-y-1">
          {calls.map((c, i) => (
            <li key={`${c.at}-${i}`} className="flex items-baseline gap-2 text-xs">
              <span className="w-14 shrink-0 tabular-nums text-text">{clockWords(c.minute)}</span>
              <span className="min-w-0 text-text-muted">
                Call {c.step + 1} of cycle {c.cycle} · {strategyText(c.strategy).label.toLowerCase()}
                {values.phases.length > 1 && <span className="font-mono"> · {values.phases[c.phase]?.name}</span>}
                {c.moved && <span className="text-text-subtle"> · {MOVED[c.moved]}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function stripEnd(v: CadenceFormValues, plan: Plan): string {
  if (plan.endedBy === "total_attempts") return `done by day ${plan.lastDay}`;
  if (plan.endedBy === "horizon") return "no end";
  const days = plan.endedBy === "expired" ? v.expire_days.trim() : v.phases[v.phases.length - 1]?.ends_days.trim();
  return `over ${days || plan.lastDay} days`;
}

/** A cadence's first three weeks in one row, for the catalog: a cell per day shaded by its
 *  calls, and how it ends. */
export function PlanStrip({ body, className }: { body: CadenceBody; className?: string }) {
  const values = React.useMemo(() => formFromBody(body, "x", "x"), [body]);
  const plan = React.useMemo(() => planCalls(values), [values]);
  const days = 21;
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex gap-0.5" aria-hidden>
        {Array.from({ length: days }, (_, i) => {
          const n = plan.perDay[i] ?? 0;
          return (
            <span
              key={i}
              className={cn("h-3 w-2 rounded-[2px]", i + 1 > plan.lastDay ? "border border-dashed border-border" : shade(n))}
            />
          );
        })}
      </div>
      <p className="text-[11px] text-text-muted">
        {plan.calls.length} calls · {stripEnd(values, plan)}
      </p>
    </div>
  );
}

